import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { registerFlush, wacht } from "@/lib/flushRegistry";
import { OORDEEL_KNOPPEN } from "./bezoekUtils";
import { BezoekKop, RegelLezen } from "./BezoekLezen";
import { Fotos } from "./Fotos";
import { BezoekAfspraakKnop } from "./BezoekAfspraakKnop";

const TEKST = { saving: "Opslaan…", dirty: "Opslaan…", error: "Opslaan mislukt, ik probeer het opnieuw", saved: "Opgeslagen ✓" };
const StatusLabel = ({ status }) => (
  <span data-testid="bezoek-save-status" className={`text-xs font-semibold whitespace-nowrap ${status === "error" ? "text-rose-600" : status === "saved" ? "text-emerald-700" : "text-zinc-500"}`}>
    {TEKST[status] || ""}
  </span>
);

const Fataal = () => (
  <div data-testid="bezoek-save-fataal" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex flex-wrap items-center gap-3">
    <span className="flex-1">Dit bezoek kan niet meer worden opgeslagen (verwijderd of geen rechten).</span>
    <button type="button" data-testid="bezoek-herladen" onClick={() => window.location.reload()} className="min-h-[44px] px-4 rounded-md bg-zinc-950 text-white font-semibold">Herladen</button>
  </div>
);

const startChecks = (b) => Object.fromEntries(b.regels.map((r) => [r.id, { oordeel: r.oordeel || null, bevinding: r.bevinding || "" }]));
const blijvend = (st) => st >= 400 && st < 500 && ![408, 409, 429].includes(st);

// v17: de auteur bewerkt. R1: alleen gewijzigde punten gaan mee (twee apparaten overschrijven elkaar niet),
// verbergen houdt 'dirty' tot de server ok zegt, en een blijvende fout (403/404) stopt het opnieuw proberen.
export const BezoekBewerken = ({ start }) => {
  const [b, setB] = useState(start);
  const [checks, setChecks] = useState(() => startChecks(start));
  const [status, setStatus] = useState("saved");
  const [fataal, setFataal] = useState(false);
  const ref = useRef(checks);
  const dirtyIds = useRef(new Set());
  const saving = useRef(false);
  const timer = useRef(null);
  const levend = useRef(true);
  const stop = useRef(false);
  const id = start.id;

  const accepteer = (d) => setB((cur) => ((d.updatedAt || "") >= (cur.updatedAt || "") ? d : cur));
  const body = () => ({ checks: Object.fromEntries([...dirtyIds.current].map((cid) => [cid, ref.current[cid]])) });
  const wisVerstuurd = (verstuurd) => {
    for (const [cid, v] of Object.entries(verstuurd.checks)) if (ref.current[cid] === v) dirtyIds.current.delete(cid);
  };
  const viaFetch = (snap) => fetch(`${API}/bezoeken/${id}`, {
    method: "PUT", keepalive: true, body: JSON.stringify(snap),
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("tw1_token")}` },
  });

  const plan = (ms) => { clearTimeout(timer.current); if (levend.current && !stop.current) timer.current = setTimeout(doSave, ms); };
  const doSave = async () => {
    if (saving.current || stop.current || dirtyIds.current.size === 0) return;
    saving.current = true; setStatus("saving");
    const snap = body();
    let fout = false;
    try {
      const r = await api.put(`/bezoeken/${id}`, snap);
      wisVerstuurd(snap);
      if (levend.current) { accepteer(r.data); setStatus(dirtyIds.current.size ? "dirty" : "saved"); }
    } catch (e) {
      fout = true;
      if (blijvend(e?.response?.status)) { stop.current = true; if (levend.current) setFataal(true); }
      else if (levend.current) { setStatus("error"); plan(4000); }
      else naUnmount();
    } finally {
      saving.current = false;
      if (!fout && dirtyIds.current.size) {
        if (levend.current) plan(800);
        else naUnmount();
      }
    }
  };
  // Na unmount: één keepalive-PUT met uitkomst; mislukt hij, dan een toast.
  const naUnmount = () => {
    if (stop.current || dirtyIds.current.size === 0) return;
    const snap = body();
    dirtyIds.current.clear();
    viaFetch(snap).then((r) => { if (!r.ok) throw new Error(String(r.status)); })
      .catch(() => toast.error("Je laatste wijziging aan het bezoek is niet opgeslagen."));
  };
  const wijzig = (cid, v, ms) => {
    const next = { ...ref.current, [cid]: { ...ref.current[cid], ...v } };
    ref.current = next; setChecks(next); dirtyIds.current.add(cid);
    if (!stop.current) { setStatus("dirty"); plan(ms); }
  };
  const flush = () => { clearTimeout(timer.current); doSave(); };
  const flushRef = useRef(null);
  flushRef.current = async () => {
    clearTimeout(timer.current);
    for (let i = 0; i < 50 && saving.current; i++) await wacht(100);
    await doSave();
  };

  useEffect(() => {
    levend.current = true;
    const verstuur = (wis) => {
      if (stop.current || saving.current || dirtyIds.current.size === 0) return;
      clearTimeout(timer.current);
      const snap = body();
      viaFetch(snap).then((r) => {
        if (!r.ok) return;
        wisVerstuurd(snap);
        if (levend.current && !saving.current) setStatus(dirtyIds.current.size ? "dirty" : "saved");
      }).catch(() => {});
      if (wis) dirtyIds.current.clear();   // alleen bij een echte page-unload
    };
    const onHide = () => verstuur(true);
    const onVis = () => { if (document.visibilityState === "hidden") verstuur(false); else if (dirtyIds.current.size) plan(300); };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVis);
    const afmelden = registerFlush(() => flushRef.current());
    return () => {
      levend.current = false;
      afmelden();
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVis);
      clearTimeout(timer.current);
      if (!saving.current) naUnmount();   // loopt er een save, dan doet die's finally het
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ontbreekt = new Set(b.bevindingOntbreekt || []);
  return (
    <div data-testid="bezoek-bewerken" className="space-y-5">
      <BezoekKop b={b} rechts={!fataal && <span className="flex items-center gap-2"><BezoekAfspraakKnop locatie={b.locatie} /><StatusLabel status={status} /></span>} />
      {fataal && <Fataal />}
      <ol className="space-y-3">
        {b.regels.map((r) => {
          if (r.vervallen) return <RegelLezen key={r.id} r={r} />;
          const c = checks[r.id] || { oordeel: null, bevinding: "" };
          return (
            <li key={r.id} data-testid={`bezoek-check-${r.id}`} className="bg-white border border-zinc-200 rounded-[10px] p-3 space-y-2.5">
              <div className="text-sm font-semibold text-zinc-800">{r.tekst}</div>
              <div className="grid grid-cols-3 gap-2" role="group" aria-label={r.tekst}>
                {OORDEEL_KNOPPEN.map(([k, label, aan]) => (
                  <button key={k} type="button" aria-pressed={c.oordeel === k} data-testid={`bezoek-oordeel-${r.id}-${k}`} disabled={fataal}
                    onClick={() => wijzig(r.id, { oordeel: c.oordeel === k ? null : k }, 300)}
                    className={`min-h-[48px] rounded-md border text-sm font-semibold transition-colors ${c.oordeel === k ? aan : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <textarea data-testid={`bezoek-bevinding-${r.id}`} aria-label={`Wat zag je bij ${r.tekst}`} value={c.bevinding} maxLength={1000} rows={2}
                placeholder="Wat zag je?" disabled={fataal}
                onChange={(e) => wijzig(r.id, { bevinding: e.target.value }, 1000)} onBlur={flush}
                className="w-full px-3 py-2 rounded-md border border-zinc-300 text-base focus:outline-none focus:ring-2 focus:ring-blue-600" />
              {ontbreekt.has(r.id) && c.oordeel === "niet" && <p data-testid={`bezoek-hint-${r.id}`} className="text-xs font-semibold text-amber-700">Schrijf op wat je zag</p>}
            </li>
          );
        })}
      </ol>
      <Fotos bezoek={b} magBewerken={!fataal} onBezoek={accepteer} />
    </div>
  );
};
